import React, { forwardRef, useEffect, useRef, useState } from "react";
import {
  Dialog as MuiDialog,
  Fade,
  IconButton,
  TextField,
} from "@mui/material";
import { CloseRounded, ReceiptLongRounded } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import Button from "./Button";

const DialogTransition = forwardRef(function DialogTransition(props, ref) {
  return <Fade ref={ref} {...props} timeout={220} />;
});

// Lightweight popup that asks the cashier for a payment reference / transaction
// number when the chosen payment method has AskForReference enabled. The
// reference is ALWAYS optional — Skip submits the payment with a blank ref.
// Confirm submits with whatever was typed. Used by the POS V1 / V2 checkout
// flows where a payment method is a single button tap (form-based screens use
// an inline field instead).
const PaymentReferenceDialog = ({
  open,
  payTypeName = "",
  onConfirm,
  onSkip,
  onClose,
}) => {
  const { t } = useTranslation(["forms", "common"]);
  const [value, setValue] = useState("");
  const inputRef = useRef(null);

  // Reset + focus the field every time the dialog opens so a previous capture
  // never leaks into the next payment.
  useEffect(() => {
    if (open) {
      setValue("");
      // defer focus until the Fade transition mounts the input
      setTimeout(() => inputRef.current?.focus(), 240);
    }
  }, [open]);

  const handleConfirm = () => onConfirm(value.trim());
  const handleSkip = () => onSkip();

  const buttonSx = {
    textTransform: "none",
    fontWeight: 600,
    fontSize: 13,
    borderRadius: "8px",
    height: 40,
    minWidth: 96,
    px: 2.25,
    boxShadow: "none",
    "&:hover": { boxShadow: "none" },
  };

  return (
    <MuiDialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      TransitionComponent={DialogTransition}
      keepMounted
      PaperProps={{
        sx: {
          borderRadius: "14px",
          overflow: "hidden",
          boxShadow: "0 16px 48px -12px rgba(15, 23, 42, 0.22)",
          maxWidth: 420,
          m: 2,
        },
      }}
    >
      {/* -------- Header -------- */}
      <div className="flex items-center justify-between px-3 py-[10px] border-b border-gray-100">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-7 h-7 rounded-[8px] bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
            <ReceiptLongRounded sx={{ fontSize: 16 }} />
          </div>
          <div className="text-[13px] font-semibold text-gray-800 leading-tight truncate">
            {t("forms:paymentReference", "Payment Reference")}
            {payTypeName ? (
              <span className="text-gray-400 font-normal"> · {payTypeName}</span>
            ) : null}
          </div>
        </div>
        <IconButton
          size="small"
          onClick={onClose}
          sx={{
            color: "#9ca3af",
            "&:hover": { backgroundColor: "#f3f4f6", color: "#4b5563" },
          }}
        >
          <CloseRounded sx={{ fontSize: 18 }} />
        </IconButton>
      </div>

      {/* -------- Body -------- */}
      <div className="px-3 py-4 bg-white">
        <TextField
          inputRef={inputRef}
          fullWidth
          value={value}
          onChange={(e) => setValue(e.target.value)}
          size="small"
          label={t("forms:referenceNo", "Reference / Transaction No")}
          placeholder={t("forms:referenceNo", "Reference / Transaction No")}
          inputProps={{ maxLength: 100 }}
          sx={{
            "& .MuiInputBase-root": {
              height: 44,
              fontSize: 14,
              borderRadius: "8px",
            },
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleConfirm();
            }
          }}
        />
        <p className="text-[11px] text-gray-400 mt-2 leading-relaxed">
          {t(
            "forms:referenceHint",
            "Enter the card / transfer / cheque reference, or skip."
          )}
        </p>
      </div>

      {/* -------- Footer -------- */}
      <div className="px-3 py-3 border-t border-gray-100 bg-gray-50/40 flex items-center justify-end gap-3">
        <Button
          onClick={handleSkip}
          variant="outlined"
          sx={{
            ...buttonSx,
            color: "#475569",
            borderColor: "#e5e7eb",
            backgroundColor: "#fff",
            "&:hover": {
              backgroundColor: "#f9fafb",
              borderColor: "#d1d5db",
              boxShadow: "none",
            },
          }}
        >
          {t("forms:skip", "Skip")}
        </Button>
        <Button onClick={handleConfirm} sx={buttonSx}>
          {t("forms:confirm", "Confirm")}
        </Button>
      </div>
    </MuiDialog>
  );
};

export default PaymentReferenceDialog;
